import type { L10n, MetricKind, SportId, SportMetric } from '../types'

/**
 * CENTRAL SPORTS CATALOGUE — numeric metrics.
 * Mobile (sport screens, evolution chart, public profile) and Admin → Performance all read
 * from here. Categorical/profile fields (belt, style, modality…) live in
 * data/sportProfileFields.ts and are never plotted. Values are prototype sample data,
 * stored in metric units; 0 = no record.
 */
export const sports: SportId[] = [
  'running',
  'cycling',
  'swimming',
  'triathlon',
  'crossfit',
  'strength',
  'yoga',
  'pilates',
  'beachTennis',
  'jiujitsu',
  'muayThai',
  'boxing',
]

/** Average weeks per month — used to derive monthly hours from weekly hours. */
export const WEEKS_PER_MONTH = 52 / 12

/** Triathlon race distances. PR records are kept separately per distance. */
export const triathlonDistances: { id: string; label: L10n }[] = [
  { id: 'superSprint', label: { 'pt-BR': 'Super Sprint', 'en-US': 'Super Sprint' } },
  { id: 'sprint', label: { 'pt-BR': 'Sprint', 'en-US': 'Sprint' } },
  { id: 'olympic', label: { 'pt-BR': 'Olímpico', 'en-US': 'Olympic' } },
  { id: 'half', label: { 'pt-BR': 'Half Ironman / 70.3', 'en-US': 'Half Ironman / 70.3' } },
  { id: 'ironman', label: { 'pt-BR': 'Ironman / 140.6', 'en-US': 'Ironman / 140.6' } },
]

const triathlonPrSamples: Record<string, number> = { sprint: 4980 }

type Extra = Pick<SportMetric, 'chart' | 'optional' | 'derived' | 'raceDistance' | 'group'>

function m(
  id: string,
  sport: SportId,
  pt: string,
  en: string,
  kind: MetricKind,
  value: number,
  extra: Extra = {}
): SportMetric {
  return { id, sport, label: { 'pt-BR': pt, 'en-US': en }, kind, value, ...extra }
}

const chart = { chart: true }

const definitions: SportMetric[] = [
  /* Running */
  m('running:maxKm', 'running', 'Maior distância', 'Max distance', 'distance', 18, chart),
  m('running:pace', 'running', 'Pace médio', 'Average pace', 'pace', 332, chart),
  m('running:longRun', 'running', 'Long run', 'Long run', 'distance', 16.4),
  m('running:split', 'running', 'Split médio', 'Average split', 'pace', 338),
  m('running:pr5k', 'running', 'PR 5 km', '5 km PR', 'duration', 1540),
  m('running:pr10k', 'running', 'PR 10 km', '10 km PR', 'duration', 2892, chart),
  m('running:pr21k', 'running', 'PR 21 km', '21 km PR', 'duration', 6750),
  m('running:pr42k', 'running', 'PR 42 km', '42 km PR', 'duration', 0),

  /* Cycling */
  m('cycling:ftp', 'cycling', 'FTP', 'FTP', 'power', 212, chart),
  m('cycling:wkg', 'cycling', 'W/kg', 'W/kg', 'ratio', 3.4, chart),

  /* Swimming */
  m('swimming:pace', 'swimming', 'Pace de natação', 'Swim pace', 'swimPace', 118, chart),
  m('swimming:splits', 'swimming', 'Split médio', 'Average split', 'swimPace', 121),
  m('swimming:strokeRate', 'swimming', 'Stroke rate', 'Stroke rate', 'plain', 34),

  /* CrossFit / Functional */
  m('crossfit:vo2', 'crossfit', 'VO2 máx', 'VO2 max', 'plain', 42, chart),
  m('crossfit:1rm', 'crossfit', 'Clean 1RM', 'Clean 1RM', 'weight', 52, chart),

  /* Strength training */
  m('strength:frequency', 'strength', 'Frequência semanal', 'Weekly frequency', 'plain', 4, chart),
  m('strength:volume', 'strength', 'Volume semanal', 'Weekly volume', 'weight', 12400, chart),
  m('strength:squat', 'strength', 'Agachamento 1RM', 'Squat 1RM', 'weight', 95, chart),
  m('strength:bench', 'strength', 'Supino 1RM', 'Bench 1RM', 'weight', 52),
  m('strength:deadlift', 'strength', 'Terra 1RM', 'Deadlift 1RM', 'weight', 120, chart),

  /* Triathlon — PR per race distance, then discipline metrics */
  ...triathlonDistances.map((distance) => ({
    id: `triathlon:pr:${distance.id}`,
    sport: 'triathlon' as SportId,
    label: {
      'pt-BR': `PR · ${distance.label['pt-BR']}`,
      'en-US': `PR · ${distance.label['en-US']}`,
    },
    kind: 'duration' as MetricKind,
    value: triathlonPrSamples[distance.id] ?? 0,
    chart: true,
    optional: true,
    raceDistance: distance.id,
  })),
  m('triathlon:swimPace', 'triathlon', 'Pace de natação', 'Swim pace', 'swimPace', 112, chart),
  m('triathlon:ftp', 'triathlon', 'FTP no ciclismo', 'Cycling FTP', 'power', 198, chart),
  m('triathlon:runPace', 'triathlon', 'Pace de corrida', 'Run pace', 'pace', 318, chart),
  m('triathlon:t1', 'triathlon', 'Transição T1', 'T1 transition', 'duration', 95, { chart: true, optional: true }),
  m('triathlon:t2', 'triathlon', 'Transição T2', 'T2 transition', 'duration', 0, { chart: true, optional: true }),

  /* Yoga — user-entered practice records, never interpreted clinically */
  m('yoga:pranayama', 'yoga', 'Pranayama · ciclos por minuto', 'Pranayama · cycles per minute', 'perMinute', 6, chart),
  m('yoga:phalakasana', 'yoga', 'Phalakasana · isometria', 'Phalakasana · isometric hold', 'seconds', 75, chart),
  m('yoga:kumbhakasana', 'yoga', 'Kumbhakasana · isometria', 'Kumbhakasana · isometric hold', 'seconds', 60, chart),
  m('yoga:utkatasana', 'yoga', 'Utkatasana · isometria', 'Utkatasana · isometric hold', 'seconds', 45, chart),
  m('yoga:vrikshasana', 'yoga', 'Vrikshasana · equilíbrio', 'Vrikshasana · balance', 'seconds', 50, chart),
  m('yoga:frequency', 'yoga', 'Frequência · sessões por semana', 'Frequency · sessions per week', 'plain', 3, chart),

  /* Pilates */
  m('pilates:plank', 'pilates', 'Prancha · isometria', 'Plank · isometric hold', 'seconds', 90, chart),
  m('pilates:frequency', 'pilates', 'Frequência · sessões por semana', 'Frequency · sessions per week', 'plain', 2, chart),

  /* Beach Tennis */
  m('beachTennis:matchesMonth', 'beachTennis', 'Jogos no mês', 'Matches this month', 'plain', 12, chart),
  m('beachTennis:longestMatch', 'beachTennis', 'Jogo mais longo', 'Longest match', 'duration', 5400, chart),

  /* Jiu-jitsu */
  m('jiujitsu:hoursWeek', 'jiujitsu', 'Treino · horas por semana', 'Training · hours per week', 'hours', 6, chart),
  m('jiujitsu:hoursMonth', 'jiujitsu', 'Horas por mês (estimado)', 'Hours per month (estimated)', 'hours', 0, {
    derived: { from: 'jiujitsu:hoursWeek', factor: WEEKS_PER_MONTH },
  }),

  /* Muay Thai */
  m('muayThai:proFights', 'muayThai', 'Lutas profissionais', 'Professional fights', 'plain', 3),
  m('muayThai:hoursWeek', 'muayThai', 'Treino · horas por semana', 'Training · hours per week', 'hours', 4, chart),

  /* Boxing */
  m('boxing:proFights', 'boxing', 'Lutas profissionais', 'Professional fights', 'plain', 8, chart),
  // Boxing record — one grouped concept (wins · losses · draws), never charted piecemeal.
  m('boxing:wins', 'boxing', 'Cartel · vitórias', 'Record · wins', 'plain', 6, { group: 'boxing:record' }),
  m('boxing:losses', 'boxing', 'Cartel · derrotas', 'Record · losses', 'plain', 1, { group: 'boxing:record' }),
  m('boxing:draws', 'boxing', 'Cartel · empates', 'Record · draws', 'plain', 1, { group: 'boxing:record' }),
  m('boxing:hoursWeek', 'boxing', 'Treino · horas por semana', 'Training · hours per week', 'hours', 5, chart),
]

/** Resolves derived metrics from their source (never stored as a separate manual entry). */
function resolveDerived(list: SportMetric[]): SportMetric[] {
  return list.map((metric) => {
    if (!metric.derived) return metric
    const source = list.find((item) => item.id === metric.derived!.from)
    const value = source ? Math.round(source.value * metric.derived.factor * 10) / 10 : 0
    return { ...metric, value }
  })
}

export const sportMetrics: SportMetric[] = resolveDerived(definitions)

export function metricsForSport(sport: SportId): SportMetric[] {
  return sportMetrics.filter((metric) => metric.sport === sport)
}

/** Numeric metrics that can be plotted (excludes derived values and grouped members). */
export function chartableMetrics(sport: SportId): SportMetric[] {
  return metricsForSport(sport).filter((metric) => metric.chart && !metric.derived && !metric.group)
}

/** Metrics shown one by one (grouped members are shown through their group). */
export function standaloneMetrics(sport: SportId): SportMetric[] {
  return metricsForSport(sport).filter((metric) => !metric.group)
}

export interface SportMetricGroup {
  id: string
  sport: SportId
  label: L10n
  members: SportMetric[]
}

const groupLabels: Record<string, L10n> = {
  'boxing:record': { 'pt-BR': 'Cartel', 'en-US': 'Record' },
}

export function metricGroupsForSport(sport: SportId): SportMetricGroup[] {
  const ids = Array.from(new Set(metricsForSport(sport).flatMap((metric) => (metric.group ? [metric.group] : []))))
  return ids.map((id) => ({
    id,
    sport,
    label: groupLabels[id] ?? { 'pt-BR': id, 'en-US': id },
    members: metricsForSport(sport).filter((metric) => metric.group === id),
  }))
}

/**
 * Historical snapshots of a grouped record (prototype sample). Each update is a dated
 * snapshot of the whole group — earlier snapshots are never rewritten.
 */
export const metricGroupHistory: Record<string, { date: string; values: Record<string, number> }[]> = {
  'boxing:record': [
    { date: '2025-11-08', values: { 'boxing:wins': 4, 'boxing:losses': 1, 'boxing:draws': 0 } },
    { date: '2026-04-19', values: { 'boxing:wins': 5, 'boxing:losses': 1, 'boxing:draws': 1 } },
    { date: '2026-08-30', values: { 'boxing:wins': 6, 'boxing:losses': 1, 'boxing:draws': 1 } },
  ],
}

export function findSportMetric(id: string): SportMetric | undefined {
  return sportMetrics.find((metric) => metric.id === id)
}
